import React, { useState, useMemo } from 'react';
import { PlanItem, PlanRestrictions } from '../../services/superAdminService';
import { usePlanes } from '../../hooks/usePlanes';

const PlanesPage: React.FC = () => {
  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActivo, setFilterActivo] = useState<'todos' | 'activos' | 'inactivos'>('todos');
  const [sortBy, setSortBy] = useState<'nombre' | 'codigo' | 'fechaCreacion'>('nombre');

  const {
    planes: data,
    isLoading,
    error,
    refetch,
    isFetching,
    createPlan,
    updatePlan,
    deletePlan,
    isCreating,
    isUpdating,
  } = usePlanes();

  const createMutation = {
    mutate: (payload: { nombre: string; codigo: string; descripcion?: string; activo?: boolean; featureFlagsJson?: string }) =>
      createPlan(payload, {
        onSuccess: () => {
          setShowCreateForm(false);
          setFormData({
            nombre: '',
            codigo: '',
            descripcion: '',
            activo: true,
            featureFlagsJson: '',
            restrictions: {
              maxPisos: 5,
              maxAreas: 10,
              maxUsuarios: 5,
              maxMensajes: 1000,
              features: []
            }
          });
        },
      }),
    isPending: isCreating,
  };

  const updateMutation = {
    mutate: ({ id, payload }: { id: number; payload: { nombre: string; descripcion?: string; activo: boolean; featureFlagsJson?: string } }) =>
      updatePlan({ id, payload }, {
        onSuccess: () => {
          setShowEditForm(false);
          setEditingPlan(null);
        },
      }),
    isPending: isUpdating,
  };

  const deleteMutation = {
    mutate: (id: number) => deletePlan(id),
  };

  // Estados del formulario
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanItem | null>(null);
  const [formData, setFormData] = useState({ 
    nombre: '', 
    codigo: '', 
    descripcion: '', 
    activo: true, 
    featureFlagsJson: '',
    restrictions: {
      maxPisos: 5,
      maxAreas: 10,
      maxUsuarios: 5,
      maxMensajes: 1000,
      features: []
    }
  });
  const [formErrors, setFormErrors] = useState<{ nombre?: string; codigo?: string }>({});

  // Validar formulario
  const validateForm = (): boolean => {
    const errors: { nombre?: string; codigo?: string } = {};
    
    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 100) {
      errors.nombre = 'El nombre no puede exceder 100 caracteres';
    }

    if (!formData.codigo.trim()) {
      errors.codigo = 'El código es requerido';
    } else if (!/^[A-Z0-9_]+$/.test(formData.codigo)) {
      errors.codigo = 'El código solo puede contener letras mayúsculas, números y guiones bajos';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Filtrar y ordenar planes
  const planesFiltrados = useMemo(() => {
    if (!data) return [];
    
    let filtered = data.filter(plan => {
      const matchesSearch = plan.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           plan.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           plan.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesActivo = filterActivo === 'todos' || 
        (filterActivo === 'activos' && plan.activo) ||
        (filterActivo === 'inactivos' && !plan.activo);
      
      return matchesSearch && matchesActivo;
    });

    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'nombre':
          return a.nombre.localeCompare(b.nombre);
        case 'codigo':
          return a.codigo.localeCompare(b.codigo);
        case 'fechaCreacion':
          return new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [data, searchTerm, filterActivo, sortBy]);



  const handleCreatePlan = () => {
    if (!validateForm()) return;
    
    const featureFlagsJson = JSON.stringify({
      restrictions: formData.restrictions,
      features: formData.restrictions.features
    });
    
    createMutation.mutate({
      nombre: formData.nombre.trim(),
      codigo: formData.codigo.trim(),
      descripcion: formData.descripcion.trim() || undefined,
      activo: formData.activo,
      featureFlagsJson
    });
  };

  const handleEditPlan = () => {
    if (!validateForm() || !editingPlan) return;
    
    const featureFlagsJson = JSON.stringify({
      restrictions: formData.restrictions,
      features: formData.restrictions.features
    });
    
    updateMutation.mutate({
      id: editingPlan.id,
      payload: {
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim() || undefined,
        activo: formData.activo,
        featureFlagsJson
      }
    });
  };

  const openEditForm = (plan: PlanItem) => {
    setEditingPlan(plan);
    
    // Parsear las restricciones del JSON
    let restrictions = {
      maxPisos: 5,
      maxAreas: 10,
      maxUsuarios: 5,
      maxMensajes: 1000,
      features: []
    };
    
    if (plan.featureFlagsJson) {
      try {
        const parsed = JSON.parse(plan.featureFlagsJson);
        restrictions = parsed.restrictions || restrictions;
      } catch (e) {
        console.error('Error parsing feature flags JSON:', e);
      }
    }
    
    setFormData({
      nombre: plan.nombre,
      codigo: plan.codigo,
      descripcion: plan.descripcion || '',
      activo: plan.activo,
      featureFlagsJson: plan.featureFlagsJson || '',
      restrictions
    });
    setShowEditForm(true);
  };

  const handleDeletePlan = (plan: PlanItem) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar el plan "${plan.nombre}"?`)) {
      deleteMutation.mutate(plan.id);
    }
  };

  const parsePlanRestrictions = (plan: PlanItem): PlanRestrictions | null => {
    if (!plan.featureFlagsJson) return null;
    
    try {
      const parsed = JSON.parse(plan.featureFlagsJson);
      return parsed.restrictions || null;
    } catch (e) {
      return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <div className="text-gray-500 mt-2">Cargando planes...</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Error al cargar los planes</div>
        <button 
          onClick={() => refetch()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Planes</h2>
          <p className="text-gray-600 mt-1">Gestiona los planes de suscripción disponibles</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => refetch()} 
            disabled={isFetching}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {isFetching ? 'Actualizando...' : 'Actualizar'}
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Nuevo Plan
          </button>
        </div>
      </div>



      {/* Filtros */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <input
              type="text"
              placeholder="Nombre, código o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={filterActivo}
              onChange={(e) => setFilterActivo(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="todos">Todos</option>
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ordenar por</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="nombre">Nombre</option>
              <option value="codigo">Código</option>
              <option value="fechaCreacion">Fecha de creación</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              {planesFiltrados.length} de {data.length} planes
            </div>
          </div>
        </div>
      </div>

      {/* Formulario de creación */}
      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Crear nuevo plan</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input
                type="text"
                placeholder="Nombre del plan"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.nombre ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.nombre && (
                <p className="text-red-600 text-sm mt-1">{formErrors.nombre}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
              <input
                type="text"
                placeholder="BASICO, INTERMEDIO, etc."
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.codigo ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.codigo && (
                <p className="text-red-600 text-sm mt-1">{formErrors.codigo}</p>
              )}
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              placeholder="Descripción del plan..."
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Restricciones */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Restricciones del Plan</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Pisos</label>
                <input
                  type="number"
                  min="1"
                  value={formData.restrictions.maxPisos}
                  onChange={(e) => setFormData({
                    ...formData,
                    restrictions: {
                      ...formData.restrictions,
                      maxPisos: parseInt(e.target.value) || 1
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Áreas</label>
                <input
                  type="number"
                  min="1"
                  value={formData.restrictions.maxAreas}
                  onChange={(e) => setFormData({
                    ...formData,
                    restrictions: {
                      ...formData.restrictions,
                      maxAreas: parseInt(e.target.value) || 1
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Usuarios</label>
                <input
                  type="number"
                  min="1"
                  value={formData.restrictions.maxUsuarios}
                  onChange={(e) => setFormData({
                    ...formData,
                    restrictions: {
                      ...formData.restrictions,
                      maxUsuarios: parseInt(e.target.value) || 1
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Mensajes</label>
                <input
                  type="number"
                  min="1"
                  value={formData.restrictions.maxMensajes}
                  onChange={(e) => setFormData({
                    ...formData,
                    restrictions: {
                      ...formData.restrictions,
                      maxMensajes: parseInt(e.target.value) || 1
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center mt-4">
            <input
              type="checkbox"
              id="activo"
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="activo" className="ml-2 text-sm text-gray-700">
              Plan activo
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreatePlan}
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creando...' : 'Crear Plan'}
            </button>
          </div>
        </div>
      )}

      {/* Formulario de edición */}
      {showEditForm && editingPlan && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Editar plan: {editingPlan.nombre}</h3>
            <button
              onClick={() => setShowEditForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input
                type="text"
                placeholder="Nombre del plan"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.nombre ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.nombre && (
                <p className="text-red-600 text-sm mt-1">{formErrors.nombre}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
              <input
                type="text"
                value={editingPlan.codigo}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">El código no se puede modificar</p>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              placeholder="Descripción del plan..."
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Restricciones */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Restricciones del Plan</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Pisos</label>
                <input
                  type="number"
                  min="1"
                  value={formData.restrictions.maxPisos}
                  onChange={(e) => setFormData({
                    ...formData,
                    restrictions: {
                      ...formData.restrictions,
                      maxPisos: parseInt(e.target.value) || 1
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Áreas</label>
                <input
                  type="number"
                  min="1"
                  value={formData.restrictions.maxAreas}
                  onChange={(e) => setFormData({
                    ...formData,
                    restrictions: {
                      ...formData.restrictions,
                      maxAreas: parseInt(e.target.value) || 1
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Usuarios</label>
                <input
                  type="number"
                  min="1"
                  value={formData.restrictions.maxUsuarios}
                  onChange={(e) => setFormData({
                    ...formData,
                    restrictions: {
                      ...formData.restrictions,
                      maxUsuarios: parseInt(e.target.value) || 1
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Máx. Mensajes</label>
                <input
                  type="number"
                  min="1"
                  value={formData.restrictions.maxMensajes}
                  onChange={(e) => setFormData({
                    ...formData,
                    restrictions: {
                      ...formData.restrictions,
                      maxMensajes: parseInt(e.target.value) || 1
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center mt-4">
            <input
              type="checkbox"
              id="activo-edit"
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="activo-edit" className="ml-2 text-sm text-gray-700">
              Plan activo
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setShowEditForm(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={handleEditPlan}
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      )}

      {/* Lista de planes */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Restricciones
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Creación
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {planesFiltrados.map(plan => {
                const restrictions = parsePlanRestrictions(plan);
                return (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{plan.nombre}</div>
                        {plan.descripcion && (
                          <div className="text-sm text-gray-500">{plan.descripcion}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {plan.codigo}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {restrictions ? (
                        <div className="text-sm text-gray-900">
                          <div>Pisos: {restrictions.maxPisos}</div>
                          <div>Áreas: {restrictions.maxAreas}</div>
                          <div>Usuarios: {restrictions.maxUsuarios}</div>
                          <div>Mensajes: {restrictions.maxMensajes.toLocaleString()}</div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Sin restricciones</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        plan.activo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {plan.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(plan.fechaCreacion).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditForm(plan)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeletePlan(plan)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {planesFiltrados.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">No se encontraron planes</div>
        </div>
      )}
    </div>
  );
};

export default PlanesPage;
